import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getAdminWorkspaceOverview,
  downloadAdminWorkspaceBackup,
  getMyProfile,
  previewAdminBackupRestore,
  restoreAdminBackup,
  uploadAdminBackupRestore,
  getAdminBackupRestoreJob,
  transferWorkspaceOwner,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import ConfirmDialog from '../../core/public/ConfirmDialog';

const RESTORE_PHASE_KEYS = {
  queued: 'settings.workspaces.restore_phase_queued',
  extracting_media: 'settings.workspaces.restore_phase_extracting_media',
  importing_db: 'settings.workspaces.restore_phase_importing_db',
  finalizing: 'settings.workspaces.restore_phase_finalizing',
  done: 'settings.workspaces.restore_phase_done',
  failed: 'settings.workspaces.restore_phase_failed',
};

const pollRestoreJob = async (jobId, onUpdate) => {
  const delay = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
  for (;;) {
    const res = await getAdminBackupRestoreJob(jobId);
    const job = res.data;
    if (onUpdate && job) {
      onUpdate(job);
    }
    if (job?.done) {
      return job;
    }
    await delay(1500);
  }
};

const workspaceOwner = (members) => (members || []).find((m) => m.role === 'OWNER');

const SettingsWorkspacesPage = () => {
  const { translate } = useTranslation();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const [restoreUploadId, setRestoreUploadId] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreWorkspaceName, setRestoreWorkspaceName] = useState('');
  const [restoreUserId, setRestoreUserId] = useState('');
  const [restoreSubmitting, setRestoreSubmitting] = useState(false);
  const [restoreUploading, setRestoreUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [restoreJob, setRestoreJob] = useState(null);

  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [ownerWorkspace, setOwnerWorkspace] = useState(null);
  const [ownerUserId, setOwnerUserId] = useState('');
  const [ownerConfirmOpen, setOwnerConfirmOpen] = useState(false);
  const [ownerSubmitting, setOwnerSubmitting] = useState(false);

  const loadOverview = async () => {
    const overview = await getAdminWorkspaceOverview();
    setUsers(overview.data?.users || []);
    setWorkspaces(overview.data?.workspaces || []);
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const me = await getMyProfile();
        if (cancelled) return;
        const p = me.data?.profile;
        setProfile(p || '');
        if (p !== 'ADMIN') {
          setLoading(false);
          return;
        }
        await loadOverview();
      } catch (e) {
        if (!cancelled) {
          setSnackbar({ open: true, message: e.message, severity: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownload = async (workspaceId) => {
    try {
      setDownloadingId(workspaceId);
      await downloadAdminWorkspaceBackup(workspaceId);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  const openOwnerDialog = (workspace) => {
    const current = workspaceOwner(workspace.members);
    setOwnerWorkspace(workspace);
    setOwnerUserId(current?.user_id || '');
    setOwnerDialogOpen(true);
  };

  const closeOwnerDialog = () => {
    if (ownerSubmitting) return;
    setOwnerDialogOpen(false);
    setOwnerWorkspace(null);
    setOwnerUserId('');
    setOwnerConfirmOpen(false);
  };

  const handleOwnerDialogNext = () => {
    if (!ownerUserId.trim()) {
      setSnackbar({ open: true, message: translate('settings.workspaces.owner_select'), severity: 'error' });
      return;
    }
    setOwnerDialogOpen(false);
    setOwnerConfirmOpen(true);
  };

  const handleOwnerTransferConfirm = async () => {
    if (!ownerWorkspace || !ownerUserId.trim()) return;
    try {
      setOwnerSubmitting(true);
      await transferWorkspaceOwner(ownerWorkspace.id, ownerUserId);
      setSnackbar({
        open: true,
        message: translate('settings.workspaces.owner_success'),
        severity: 'success',
      });
      closeOwnerDialog();
      await loadOverview();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setOwnerSubmitting(false);
      setOwnerConfirmOpen(false);
    }
  };

  const handleRestoreFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      setRestoreUploading(true);
      setUploadPercent(0);
      const uploadRes = await uploadAdminBackupRestore(f, (loaded, total) => {
        if (total > 0) {
          setUploadPercent(Math.min(100, Math.round((loaded / total) * 100)));
        }
      });
      const uploadId = uploadRes.data?.upload_id;
      if (!uploadId) {
        throw new Error(translate('settings.workspaces.restore_preview_error'));
      }
      const res = await previewAdminBackupRestore(uploadId);
      setRestoreUploadId(uploadId);
      setRestoreWorkspaceName(res.data?.suggested_workspace_name || '');
      setRestoreUserId('');
      setRestoreJob(null);
      setRestoreDialogOpen(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || translate('settings.workspaces.restore_preview_error'),
        severity: 'error',
      });
    } finally {
      setRestoreUploading(false);
      setUploadPercent(0);
    }
  };

  const closeRestoreDialog = () => {
    if (restoreSubmitting) return;
    setRestoreDialogOpen(false);
    setRestoreUploadId(null);
    setRestoreWorkspaceName('');
    setRestoreUserId('');
    setRestoreJob(null);
  };

  const restorePhaseLabel = (phase) => {
    const key = RESTORE_PHASE_KEYS[phase];
    return key ? translate(key) : phase;
  };

  const handleRestoreSubmit = async () => {
    if (!restoreUploadId || !restoreUserId.trim()) {
      setSnackbar({ open: true, message: translate('settings.workspaces.restore_link_user'), severity: 'error' });
      return;
    }
    try {
      setRestoreSubmitting(true);
      setRestoreJob({ phase: 'queued', files_processed: 0, done: false });
      const startRes = await restoreAdminBackup(restoreUploadId, restoreWorkspaceName, restoreUserId);
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        throw new Error(translate('settings.workspaces.restore_preview_error'));
      }
      const finalJob = await pollRestoreJob(jobId, (job) => setRestoreJob(job));
      setRestoreJob(finalJob);
      if (finalJob.error) {
        throw new Error(finalJob.error);
      }
      setSnackbar({
        open: true,
        message: translate('settings.workspaces.restore_success'),
        severity: 'success',
      });
      closeRestoreDialog();
      await loadOverview();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setRestoreSubmitting(false);
    }
  };

  if (!loading && profile && profile !== 'ADMIN') {
    return <Navigate to="/settings" replace />;
  }

  const ownerConfirmDetail = ownerWorkspace?.is_personal
    ? `${translate('settings.workspaces.owner_confirm_message')}\n\n${translate('settings.workspaces.owner_personal_warning')}`
    : translate('settings.workspaces.owner_confirm_message');

  return (
    <Box>
      {loading ? (
        <Typography color="text.secondary">{translate('common.loading')}</Typography>
      ) : profile === 'ADMIN' ? (
        <>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            {translate('settings.workspaces.restore_section')}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              style={{ display: 'none' }}
              onChange={handleRestoreFileChange}
            />
            <Button
              variant="outlined"
              disabled={restoreUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {restoreUploading
                ? translate('settings.workspaces.restore_uploading')
                : translate('settings.workspaces.restore_pick_file')}
            </Button>
            {restoreUploading && (
              <Box sx={{ mt: 2, maxWidth: 480 }}>
                <LinearProgress variant="determinate" value={uploadPercent} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {translate('settings.workspaces.restore_upload_progress', { percent: uploadPercent })}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {translate('settings.workspaces.workspaces_title')}
          </Typography>
          <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('workspace.name')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{translate('settings.workspaces.owner')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{translate('settings.workspaces.members')}</TableCell>
                  <TableCell align="right">{translate('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workspaces.map((w) => {
                  const owner = workspaceOwner(w.members);
                  return (
                    <TableRow key={w.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{w.name}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ maxWidth: { xs: 160, sm: 240 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {w.id}
                        </Typography>
                        {w.is_personal && (
                          <Chip size="small" label={translate('workspace.personal')} sx={{ mt: 0.5 }} variant="outlined" />
                        )}
                        <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {translate('settings.workspaces.owner')}
                            {': '}
                            {owner ? owner.email : translate('settings.workspaces.owner_none')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {owner ? (
                          <Chip size="small" label={owner.email} color="primary" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {translate('settings.workspaces.owner_none')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {w.members && w.members.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {w.members.map((m) => (
                              <Chip
                                key={`${w.id}-${m.user_id}`}
                                size="small"
                                label={`${m.email} (${m.role})`}
                                variant="outlined"
                                sx={{ maxWidth: 280, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {translate('settings.workspaces.no_members')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={downloadingId === w.id}
                            onClick={() => handleDownload(w.id)}
                          >
                            {downloadingId === w.id
                              ? translate('settings.workspaces.downloading')
                              : translate('settings.workspaces.download')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openOwnerDialog(w)}
                          >
                            {translate('settings.workspaces.owner_change')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}

      <Dialog open={ownerDialogOpen} onClose={closeOwnerDialog} fullWidth maxWidth="sm">
        <DialogTitle>{translate('settings.workspaces.owner_dialog_title')}</DialogTitle>
        <DialogContent>
          {ownerWorkspace && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {ownerWorkspace.name}
            </Typography>
          )}
          {ownerWorkspace?.is_personal && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {translate('settings.workspaces.owner_personal_warning')}
            </Alert>
          )}
          <FormControl fullWidth margin="normal" disabled={ownerSubmitting}>
            <InputLabel id="owner-user-label">{translate('settings.workspaces.owner_select')}</InputLabel>
            <Select
              labelId="owner-user-label"
              label={translate('settings.workspaces.owner_select')}
              value={ownerUserId}
              onChange={(ev) => setOwnerUserId(ev.target.value)}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.email} ({u.profile})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOwnerDialog} disabled={ownerSubmitting}>
            {translate('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleOwnerDialogNext} disabled={ownerSubmitting || !ownerUserId}>
            {translate('common.next')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={ownerConfirmOpen}
        title={translate('settings.workspaces.owner_confirm_title')}
        message={ownerConfirmDetail}
        onConfirm={handleOwnerTransferConfirm}
        onCancel={() => {
          setOwnerConfirmOpen(false);
          setOwnerDialogOpen(true);
        }}
        confirmText={translate('settings.workspaces.owner_change')}
        cancelText={translate('common.cancel')}
        confirmColor="primary"
      />

      <Dialog open={restoreDialogOpen} onClose={closeRestoreDialog} fullWidth maxWidth="sm">
        <DialogTitle>{translate('settings.workspaces.restore_dialog_title')}</DialogTitle>
        <DialogContent>
          {restoreSubmitting && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {translate('settings.workspaces.restore_progress_hint')}
              </Typography>
              <LinearProgress />
              {restoreJob && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {restorePhaseLabel(restoreJob.phase)}
                  {restoreJob.files_processed > 0
                    ? ` — ${translate('settings.workspaces.restore_files_progress', { files: restoreJob.files_processed })}`
                    : ''}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            margin="normal"
            fullWidth
            label={translate('settings.workspaces.restore_workspace_name')}
            value={restoreWorkspaceName}
            onChange={(ev) => setRestoreWorkspaceName(ev.target.value)}
            disabled={restoreSubmitting}
          />
          <FormControl margin="normal" fullWidth disabled={restoreSubmitting}>
            <InputLabel id="restore-user-label">{translate('settings.workspaces.restore_link_user')}</InputLabel>
            <Select
              labelId="restore-user-label"
              label={translate('settings.workspaces.restore_link_user')}
              value={restoreUserId}
              onChange={(ev) => setRestoreUserId(ev.target.value)}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.email} ({u.profile})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRestoreDialog} disabled={restoreSubmitting}>
            {translate('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={restoreSubmitting || !restoreUploadId}
            onClick={handleRestoreSubmit}
          >
            {restoreSubmitting
              ? translate('settings.workspaces.restore_in_progress')
              : translate('settings.workspaces.restore_submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsWorkspacesPage;
